// One result card — the surface a trader actually chooses a package by.
//
// Drawn to the approved G0 design, which REPLACES the feature request's R2 and R3. Where the request
// document and the drawing disagree, the drawing wins (ruling L79).
//
// TWO CITATIONS, and deliberately no revision number or date:
//   the drawing  https://claude.ai/code/artifact/769bae85-574d-4ec4-b72e-33d3e9147479
//                — the URL is stable across every republish and always resolves to the current one;
//   the rulings  L79-L84 on the coordination board — immutable, and they carry the substance in words.
//
// A pinned revision ("revision 3") or a pinned date rots silently: a superseded citation reads exactly
// like a current one, so it is worse than no number at all. The sketch SHOWS, the rulings BIND, and a
// ruling that gets replaced says so in its own row. The drawing is authoritative for THE CARD, not
// for the panel frame — the column proportions are S6's.
//
//   R2 -> L80   ONE price on the card, the LEDGER price, top-right. The raw figure is gone entirely.
//               A and H marks under the date; score below the price.
//   R3 -> L81   The breakdown mirrors the two ORDERS the trader sends — Adjustment, Hedges with the
//               close-out netted in — rather than the three figures the engine produces.
//               ⚠ SUPERSEDED IN PART (owner, vrequest 2026-09-13, second pass): the Total lines are
//               gone from the breakdown; the headline price is the one total, and the breakdown
//               closes with the Score row.
//               ⚠ SUPERSEDED FOR THE CLOSE CASE (owner, vrequest 2026-09-13, third pass): when the
//               run was sent with Existing Hedges = Close, the breakdown shows the three figures —
//               Adjustment / New hedges / Closing hedges. Ignore keeps the two netted lines. The
//               Setup CHOICE decides the shape, not the close-out figure.
//   L82         Date reads `Fri 18-Sep-26 (7d)` — and the days-to-expiration is the BACKEND's.
//   L83         The order price WAS derived here from the legs on the card. No longer shown, so no
//               longer derived — `mavenOrderPrice` stays in the formatter, unused by this card.
//
// ⚠ EVERY FIGURE ON THIS CARD IS PER UNIT. Ledger scale, the net over the portfolio's default
// quantity. There is no multiplication by 100 anywhere in this component or its formatter, and none
// belongs here — that error was made once in the design and reached the owner as $2,840 for a
// package worth $28.40.

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output
} from '@angular/core';
import {Subscription} from 'rxjs';
import {MavenAppliedMarks, MavenAppliedMarksService, mavenCardKey, MavenSideMarks} from './maven-applied-marks.service';
import {MavenCard} from './maven-delivery.contract';
import {MavenLivePrice} from './maven-card-pricing.service';
import {mavenAdjustmentTypesText, mavenCardDateText, mavenMoneyText} from './maven-card-format';
import {MavenExistingHedges} from '../maven-domain';

/** Three states, and only three (R9). There is no stale card and no crossed-out card. */
export type MavenCardState = 'notReady' | 'ready' | 'selected';

@Component({
    selector: 'ets-maven-card',
    templateUrl: './maven-card.component.html',
    styleUrls: ['./maven-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MavenCardComponent implements AfterViewInit, OnDestroy {

    @Input() card: MavenCard | null = null;

    /** Live figures for this card, or null before the first tick resolves. */
    @Input() live: MavenLivePrice | null = null;

    /**
     * The Existing Hedges choice the run was sent with. 'close' opens the three-line breakdown
     * (Adjustment / New hedges / Closing hedges); anything else keeps the two-line one with the
     * close-out netted into Hedges. Null defaults to two lines — the safe shape, and the one every
     * existing caller and test was built against.
     */
    @Input() existingHedges: MavenExistingHedges | null = null;

    /**
     * The A and H marks, read by THIS CARD from the marks store — not handed down as an input.
     *
     * ⚠ THE LEAF OWNS ITS CHANGE DETECTION. This card is OnPush under an OnPush column inside a
     * workspace that detaches its own change detector, so nothing above it runs a tick for a mark
     * that changes on a message-bus event (a pop-up's save, a clear in the APG or the Hedging
     * Grid). The column's own `detectChanges()` was not enough: the marks stayed as they were until
     * the trader clicked a card, which is the only event this subtree ever gets (vrequest
     * 2026-09-13, second pass). So the card subscribes to the store itself and repaints its own
     * view — the same shape as the APG Price Box cell and the ORION snippet card, which is how this
     * codebase keeps an OnPush leaf live.
     */
    get marks(): MavenAppliedMarks {
        return this._marks.marksFor(this.card);
    }

    /**
     * True when ANOTHER card is applied — some half of its A or H is lit — so this card's orders are
     * read-only until that saved state is cleared. The card dims to say so (owner, 2026-09-15); it
     * stays selectable, and its Summary still fills.
     */
    get lockedByAnotherCard(): boolean {

        const applied = this._marks.appliedCardKey;

        return applied !== null && !!this.card && applied !== mavenCardKey(this.card);
    }

    /** The tooltip of one mark: which sides were copied from this card, in words. */
    markTitle(part: string, sides: MavenSideMarks): string {

        if (sides.calls && sides.puts) {
            return `${part}: Calls and Puts copied from this card`;
        }

        if (sides.calls) {
            return `${part}: Calls copied from this card`;
        }

        if (sides.puts) {
            return `${part}: Puts copied from this card`;
        }

        return `${part} order`;
    }

    private _marksSubscription: Subscription | null = null;

    constructor(
        private readonly _marks: MavenAppliedMarksService,
        private readonly _cdr: ChangeDetectorRef
    ) {
    }

    // Subscribed AFTER the view exists, never in the constructor: the store's `changed` is a
    // BehaviorSubject, so subscribing replays at once, and a `detectChanges()` on a view that has
    // not been created yet is undefined behaviour.
    ngAfterViewInit(): void {
        this._marksSubscription = this._marks.changed.subscribe(() => this._cdr.detectChanges());
    }

    ngOnDestroy(): void {
        if (this._marksSubscription !== null) {
            this._marksSubscription.unsubscribe();
            this._marksSubscription = null;
        }
    }

    /**
     * The score, 0-100, or null when there is none. Supplied by the column, never computed here:
     * the card does not know the other cards, and the score is relative to them.
     *
     * ⚠ PLACEHOLDER TODAY. No score travels on the delivery; the column derives one from the cost
     * per day (owner, third pass 2026-09-13) until the backend delivers a real one. See
     * `mavenPlaceholderScore` in maven-card-format.ts.
     */
    @Input() score: number | null = null;

    @Input() selected = false;

    @Output() picked = new EventEmitter<MavenCard>();

    /**
     * The card's state. Not ready means no price — a card with no total cannot be ranked, cannot be
     * chosen, and shows no figures at all rather than showing them as zero.
     */
    get state(): MavenCardState {

        if (this.ledgerTotal === null) {
            return 'notReady';
        }

        return this.selected ? 'selected' : 'ready';
    }

    get isNotReady(): boolean {
        return this.state === 'notReady';
    }

    /** The rank badge — the RUN's rank, never the row's position. An em dash while unpriced. */
    get rankText(): string {
        if (this.isNotReady || !this.card) {
            return '—';
        }

        return String(this.card.rank);
    }

    /**
     * `Fri 18-Sep-26 (7d)`. The days-to-expiration is the delivery's own figure — the platform's
     * date helper would compute a second one, and two authors for one number is the fault this
     * build has already undone twice (ruling L82).
     */
    get dateText(): string {
        if (!this.card) {
            return '';
        }

        return mavenCardDateText(this.card.date, this.card.dte);
    }

    /** The headline: the LEDGER price, per unit. The only price on the card. */
    get ledgerTotal(): number | null {

        if (this.live && this.live.total !== null && this.live.total !== undefined) {
            return this.live.total;
        }

        const delivered = this.card?.prices?.total;

        return (delivered === null || delivered === undefined) ? null : delivered;
    }

    get ledgerTotalText(): string {
        return mavenMoneyText(this.ledgerTotal);
    }

    /**
     * Cost per day — the run's ranking key, on the card under the price (owner, vrequest
     * 2026-09-13; REPLACES ruling D2, which kept it in the Details column). The delivery's own
     * figure: a tick carries no per-day term, so this does not move with the live price.
     */
    get perDay(): number | null {
        const perDay = this.card?.prices?.perDay;

        return (perDay === null || perDay === undefined) ? null : perDay;
    }

    get perDayText(): string {
        const perDay = this.perDay;

        return perDay === null ? '—' : mavenMoneyText(perDay) + ' / day';
    }

    /** The fraction's numerator. A dash when there is no score — never a zero. */
    get scoreText(): string {
        return (this.score === null || this.score === undefined) ? '—' : String(this.score);
    }

    get adjustment(): number | null {
        return this.figure('adjustment');
    }

    get adjustmentText(): string {
        return mavenMoneyText(this.adjustment);
    }

    /**
     * The adjustment types this package represents — `2B(C) + 2B(P)` — on the Adjustment line
     * (owner, 2026-09-21). The APG ORION's delivered strings, joined as its snippet card joins
     * them; the Summary's Pros shows the same string from the same formatter. Null = no chip.
     */
    get adjustmentTypesText(): string | null {
        return mavenAdjustmentTypesText(this.card?.adjustment?.adjustmentTypes);
    }

    /** The Hedges line carries the close-out netted in (ruling L81) — the Ignore shape. */
    get hedgesLine(): number | null {
        return this.figure('hedgesLine');
    }

    get hedgesText(): string {
        return mavenMoneyText(this.hedgesLine);
    }

    /**
     * True when the run CLOSED the existing hedges: the breakdown then splits into New hedges and
     * Closing hedges (owner, vrequest 2026-09-13, third pass — REPLACES L81 for this case). The
     * Setup choice decides, never the figure: a close-out of zero still gets its line.
     */
    get showsClosingHedges(): boolean {
        return this.existingHedges === 'close';
    }

    /** The OPENING hedges alone — live when a tick has priced them, else as delivered. */
    get newHedges(): number | null {
        return this.figure('hedges');
    }

    get newHedgesText(): string {
        return mavenMoneyText(this.newHedges);
    }

    /**
     * The close-out: LIVE from the delivered close legs once a tick has priced them
     * (CR-20260916-003, ruling L63 lifted), else as delivered. Null renders a dash.
     */
    get closingHedges(): number | null {
        if (this.live && this.live.closeOut !== null && this.live.closeOut !== undefined) {
            return this.live.closeOut;
        }

        const close = this.card?.prices?.closeOut;

        return (close === null || close === undefined) ? null : close;
    }

    get closingHedgesText(): string {
        return mavenMoneyText(this.closingHedges);
    }

    /**
     * The P&L colour of a figure: credit green, debit red, and NO class for zero or unknown — a dash
     * and a flat zero both stay grey. Every money figure on the card goes through this, the
     * headline included (owner, vrequest 2026-09-13: "all the numbers here must be colored
     * according to the P&L rule").
     */
    signClass(value: number | null): 'credit' | 'debit' | null {

        if (value === null || value === undefined) {
            return null;
        }

        if (value > 0) {
            return 'credit';
        }

        return value < 0 ? 'debit' : null;
    }

    get isCredit(): boolean {
        const total = this.ledgerTotal;

        return total !== null && total > 0;
    }

    get isDebit(): boolean {
        const total = this.ledgerTotal;

        return total !== null && total < 0;
    }

    /**
     * The report on the card's OWN date, when the calendar calls it tier 1 or tier 2 (ruling L14).
     *
     * It names the report and nothing else. It NEVER says the day was restored or brought back —
     * no marker for that travels, and explaining why a day survived is the reasoning the panel does
     * not show. Clear and unknown days get no chip at all; an unknown day is never dressed as clear.
     */
    get reportText(): string | null {

        const own = (this.card?.nearbyReports || []).filter(r => r && r.date === this.card.date);

        if (own.length === 0) {
            return null;
        }

        const entry = own[0];

        if (entry.tier !== 'tier1' && entry.tier !== 'tier2') {
            return null;
        }

        const label = entry.tier === 'tier1' ? 'TIER 1' : 'TIER 2';
        const events = (entry.events || []).join(', ');

        return events ? label + ' · ' + events : label;
    }

    onPick(): void {
        // A card with no price cannot be chosen: selecting it would fill the Details column with a
        // package the trader cannot act on.
        if (this.isNotReady || !this.card) {
            return;
        }

        this.picked.emit(this.card);
    }

    private figure(key: 'adjustment' | 'hedges' | 'hedgesLine'): number | null {

        if (this.live) {
            const liveValue = this.live[key];

            if (liveValue !== null && liveValue !== undefined) {
                return liveValue;
            }
        }

        if (!this.card || !this.card.prices) {
            return null;
        }

        if (key === 'adjustment') {
            return this.card.prices.adjustment;
        }

        if (key === 'hedges') {
            const opening = this.card.prices.hedges;

            return (opening === null || opening === undefined) ? null : opening;
        }

        // Delivered fall-back for the netted line: the two terms the backend sent, summed the same
        // way the live one is.
        const hedges = this.card.prices.hedges;

        if (hedges === null || hedges === undefined) {
            return null;
        }

        const close = this.card.prices.closeOut;

        return hedges + ((close === null || close === undefined) ? 0 : close);
    }
}
